/**
 * Home Page
 *
 * The main search page of the application.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAuth } from '../auth';
import { SearchBar, SearchFilterChips, SearchResults, StatusMessage, FeedbackWidget } from '../components';
import { getSelectedLanguage } from '../components/LanguageSelector';
import {
  KeywordDrawer,
  RefineByTopicButton,
  TOPIC_DRAWER_PUSH_QUERY,
  TOPIC_DRAWER_WIDTH,
} from '../components/search';
import { APP_BAR_SEARCH_SLOT_ID } from '../components/TopAppBar';
import { requestSearchBarFocus } from '../components/search/searchBarEvents';
import { SearchTipLine } from '../components/search/SearchTips';
import { useSearchController } from '../hooks/useSearchController';
import { loadSnapshot, saveSnapshot, type SearchSnapshot } from '../searchCore/searchSnapshot';
import { buildSearchParams, parseSearchUrl } from '../searchCore/urlState';
import { trackEvent } from '../services/googleAnalytics';

/** React Router's key for a history entry it did not create (direct visit). */
const DEFAULT_HISTORY_KEY = 'default';

/** Left gutter of the result column (sm and up); holds the "Refine by topic" button. */
const RESULT_GUTTER = 264;
/** Width of the result column including its right padding. */
const RESULT_COLUMN_WIDTH = 876;
/** Height of a filter chip; the "Refine by topic" button is centred on the first chip row. */
const FILTER_ROW_HEIGHT = 32;

type ScrollTarget = Pick<SearchSnapshot, 'scrollY' | 'anchor'>;

/** Current scroll position plus the first result card at least partly in view. */
function captureScroll(): ScrollTarget {
  const scrollY = window.scrollY;
  if (scrollY === 0) return { scrollY };
  for (const el of document.querySelectorAll<HTMLElement>('[data-result-id]')) {
    const rect = el.getBoundingClientRect();
    if (rect.bottom > 0) {
      return { scrollY, anchor: { resultId: el.dataset.resultId ?? '', top: rect.top } };
    }
  }
  return { scrollY };
}

/** Put the anchored result back at its viewport offset, else fall back to scrollY. */
function applyScroll({ scrollY, anchor }: ScrollTarget): void {
  const el = anchor && document.querySelector(`[data-result-id="${CSS.escape(anchor.resultId)}"]`);
  const y = anchor && el ? window.scrollY + el.getBoundingClientRect().top - anchor.top : scrollY;
  window.scrollTo(0, Math.max(0, y));
}

// How the document itself was loaded, and on which history entry. A new visit
// ('navigate') keeps `history.state` when the URL is unchanged, so only
// 'back_forward' may show cached results; 'reload' fetches fresh results but
// keeps the scroll position. Consumed by the first entry the page handles.
let documentLoad: { key: string; type: string } | null = (() => {
  const [entry] = performance.getEntriesByType?.('navigation') ?? [];
  const type = (entry as PerformanceNavigationTiming | undefined)?.type;
  const key = (window.history.state as { key?: unknown } | null)?.key;
  return type && typeof key === 'string' ? { key, type } : null;
})();

export function HomePage() {
  const { t } = useTranslation('search');
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoading: authLoading, user } = useAuth();
  const identity = user?.id ?? 'anonymous';

  const {
    query,
    setQuery,
    filters,
    setFilters,
    filtersRef,
    results,
    isLoading,
    error,
    hasSearched,
    page,
    totalPages,
    activeSearch,
    navigation,
    keywords,
    phraseHint,
    search,
    restore,
    applyFilters,
    goToPage,
    toggleKeyword,
    clearKeywords,
    clearAllFilters,
    reset,
  } = useSearchController();
  const topicDrawer = useTopicDrawer();

  // Latest values for callbacks and effects that must not re-run on every change.
  const latest = useRef({
    location,
    searchParams,
    setSearchParams,
    activeSearch,
    results,
    isLoading,
    identity,
  });
  latest.current = { location, searchParams, setSearchParams, activeSearch, results, isLoading, identity };

  // History entry whose URL has been applied; null until the first one.
  const handledHistoryKey = useRef<string | null>(null);
  // Scroll position to restore once the results for `url` are rendered.
  const pendingScroll = useRef<{ url: string; target: ScrollTarget } | null>(null);

  // Reset state when navigating back to home via logo click
  useEffect(() => {
    if (location.state?.resetKey) {
      reset();
      setSearchParams({}, { replace: true });
    }
  }, [location.state?.resetKey, reset, setSearchParams]);

  // Apply the search state from the URL on mount and on Back/Forward. A saved
  // snapshot of the history entry restores the previous results and scroll
  // position; otherwise the search runs. Waits for auth so the request carries
  // the token and the snapshot can be matched to the user.
  // Supports ?q=<query>&source=<v>&source_type=<v>&zenon_version=<v>&language=<v>
  // &created_after=<d>&created_before=<d>&updated_after=<d>&updated_before=<d>&page=<n>
  useEffect(() => {
    if (authLoading || handledHistoryKey.current === location.key) return;
    const isFirst = handledHistoryKey.current === null;
    handledHistoryKey.current = location.key;
    const loadType = documentLoad?.key === location.key ? documentLoad.type : null;
    documentLoad = null;
    // A search submitted while auth was still loading already owns the URL.
    if (isFirst && latest.current.activeSearch) return;
    // Our own URL updates (REPLACE) and the logo reset (PUSH) need no handling.
    if (!isFirst && navigationType !== 'POP') return;

    const urlState = parseSearchUrl(searchParams);
    if (!urlState.query.trim()) {
      if (!isFirst) reset();
      return;
    }

    const url = buildSearchParams(urlState).toString();
    const restorable =
      navigationType === 'POP' &&
      location.key !== DEFAULT_HISTORY_KEY &&
      (loadType === null || loadType === 'back_forward' || loadType === 'reload');
    const snapshot = restorable
      ? loadSnapshot(location.key, { url, identity, language: getSelectedLanguage() })
      : null;
    if (snapshot) {
      pendingScroll.current = { url, target: snapshot };
      if (loadType !== 'reload') {
        restore(urlState, snapshot.response);
        return;
      }
    }

    setQuery(urlState.query);
    setFilters(urlState.filters);
    filtersRef.current = urlState.filters;
    search(urlState.query, urlState.page, urlState.keywords);
  }, [
    authLoading,
    location.key,
    navigationType,
    searchParams,
    identity,
    reset,
    restore,
    search,
    setQuery,
    setFilters,
    filtersRef,
  ]);

  // Mirror the active search (query, filters, page) into the URL. Reacts only
  // to search changes (`setSearchParams` changes with every navigation) so URL
  // changes from Back/Forward or the logo reset are not overwritten.
  useEffect(() => {
    if (!activeSearch) return;
    const { searchParams: current, location: loc, setSearchParams: setParams } = latest.current;
    const next = buildSearchParams(activeSearch);
    // A direct visit has no history key of its own; replacing assigns one so
    // the entry's snapshot can be found again.
    if (next.toString() !== current.toString() || loc.key === DEFAULT_HISTORY_KEY) {
      setParams(next, { replace: true });
    }
  }, [activeSearch]);

  // Restore the saved scroll position once the matching results are rendered.
  useEffect(() => {
    const pending = pendingScroll.current;
    if (!pending || isLoading || !activeSearch) return;
    pendingScroll.current = null;
    // A different search was started in the meantime: keep its scroll position.
    if (buildSearchParams(activeSearch).toString() !== pending.url || !results) return;
    requestAnimationFrame(() => applyScroll(pending.target));
  }, [results, isLoading, activeSearch]);

  // Save results and scroll position for the history entry being left.
  const saveCurrentSnapshot = useCallback((historyKey: string, urlSearch: string) => {
    const { activeSearch: active, results: res, isLoading: loading, identity: id } = latest.current;
    if (historyKey === DEFAULT_HISTORY_KEY || !active || !res || loading) return;
    const url = buildSearchParams(active).toString();
    // Only save results that belong to the entry's URL (not e.g. a pending URL update).
    if (url !== new URLSearchParams(urlSearch).toString()) return;
    saveSnapshot(historyKey, {
      url,
      identity: id,
      language: getSelectedLanguage(),
      response: res,
      ...captureScroll(),
      savedAt: Date.now(),
    });
  }, []);

  // Leaving the entry in-app (unmount, logo click, Back/Forward). Layout cleanup
  // runs before the DOM changes and the next page scrolls, so scrollY is intact.
  useLayoutEffect(() => {
    const { key, search: urlSearch } = location;
    return () => saveCurrentSnapshot(key, urlSearch);
  }, [location, saveCurrentSnapshot]);

  // Leaving the document (external link in the same tab, reload, closing the tab).
  useEffect(() => {
    const handlePageHide = () => {
      const { key, search: urlSearch } = latest.current.location;
      saveCurrentSnapshot(key, urlSearch);
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [saveCurrentSnapshot]);

  /** Called when pagination changes. */
  /** A query derived from the results (condition removed, fix, exact phrase). */
  const searchQuery = useCallback(
    (nextQuery: string) => {
      setQuery(nextQuery);
      search(nextQuery);
    },
    [setQuery, search]
  );

  const editQuery = useCallback(
    (nextQuery: string) => {
      setQuery(nextQuery);
      requestSearchBarFocus();
    },
    [setQuery]
  );

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, newPage: number) => {
      goToPage(newPage);
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [goToPage]
  );

  // Determine if we should show the centered layout (no results yet)
  const showCenteredLayout = !hasSearched && !results;
  // The drawer only exists in the result view and while the search returns keyword data.
  const showTopics = !showCenteredLayout && navigation !== null;
  const drawerBeside = showTopics && topicDrawer.open && topicDrawer.push;

  return (
    <Box sx={{ py: 4 }}>
      {/* Centered Layout - Before Search */}
      {showCenteredLayout && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            px: { xs: 2, sm: 3 },
            color: 'primary.main',
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            textAlign="center"
            fontWeight={400}
          >
            {t('headline')}
          </Typography>
          <Box sx={{ height: 16 }} />

          <Box sx={{ width: '100%', maxWidth: 800 }}>
            <SearchBar
              query={query}
              onQueryChange={setQuery}
              onSearch={search}
              isLoading={isLoading}
              showAllFiltersButton
              filters={filters}
              onFiltersChange={setFilters}
            />
            <SearchTipLine />
          </Box>
        </Box>
      )}

      {/* Results Layout - After Search */}
      {!showCenteredLayout && (
        <Box
          sx={{
            // Beside the open drawer, the column moves right but keeps its width.
            maxWidth: (drawerBeside ? TOPIC_DRAWER_WIDTH + 32 : RESULT_GUTTER) + RESULT_COLUMN_WIDTH,
            pl: { xs: 2, sm: `${drawerBeside ? TOPIC_DRAWER_WIDTH + 32 : RESULT_GUTTER}px` },
            pr: 3,
            transition: (theme) => theme.transitions.create(['padding-left', 'max-width']),
          }}
        >
          {/* SearchBar portalled into the AppBar */}
          {(() => {
            const slot = document.getElementById(APP_BAR_SEARCH_SLOT_ID);
            if (!slot) return null;
            return createPortal(
              <SearchBar
                query={query}
                onQueryChange={setQuery}
                onSearch={search}
                isLoading={isLoading}
              />,
              slot
            );
          })()}

          {/* Filter row. "Refine by topic" sits on the same row: in the left gutter,
              right-aligned so a long label (German) never runs into the chips; on xs above them. */}
          <Box sx={{ mb: 3, position: 'relative' }}>
            {showTopics && !topicDrawer.open && (
              <Box
                sx={{
                  position: { xs: 'static', sm: 'absolute' },
                  right: { sm: '100%' },
                  top: { sm: 0 },
                  height: { sm: FILTER_ROW_HEIGHT },
                  display: 'flex',
                  alignItems: 'center',
                  mr: { sm: 2 },
                  mb: { xs: 2, sm: 0 },
                }}
              >
                <RefineByTopicButton selectedCount={keywords.length} onClick={() => topicDrawer.setOpen(true)} />
              </Box>
            )}
            <SearchFilterChips
              filters={filters}
              onChange={applyFilters}
              keywordCount={keywords.length}
              onClearAll={clearAllFilters}
            />
          </Box>

          {/* Error Message */}
          {error && (
            <StatusMessage event="server-error" />
          )}

          {/* Search Results */}
          {!error && (
            <SearchResults
              results={results}
              isLoading={isLoading}
              query={query}
              unnarrowedTotal={navigation && keywords.length > 0 ? navigation.entries.length : null}
              onSearchQuery={searchQuery}
              onEditQuery={editQuery}
              phraseHint={phraseHint}
            />
          )}

          {/* Pagination */}
          {!error && results && totalPages > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                    fontWeight: 500,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {showTopics && (
        <KeywordDrawer
          open={topicDrawer.open}
          push={topicDrawer.push}
          navigation={navigation}
          selected={keywords}
          onToggle={toggleKeyword}
          onClear={clearKeywords}
          onClose={topicDrawer.close}
        />
      )}

      {/* Floating feedback button — only on the result view, per the concept */}
      {!showCenteredLayout && <FeedbackWidget searchQuery={query} />}
    </Box>
  );
}

/**
 * Open state of the keyword drawer. Beside the results (wide screens) it is
 * remembered in localStorage, so users relying on it needn't reopen it for
 * every search. As an overlay (narrow screens) it always starts closed, so it
 * doesn't cover the results of each new search.
 */
function useTopicDrawer() {
  const push = useMediaQuery(TOPIC_DRAWER_PUSH_QUERY);
  const [remembered, setRemembered] = useState(() => readDrawerOpen());
  const [overlayOpen, setOverlayOpen] = useState(false);
  const open = push ? remembered : overlayOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (next) trackEvent('topics_drawer_open');
      if (push) {
        setRemembered(next);
        writeDrawerOpen(next);
      } else {
        setOverlayOpen(next);
      }
    },
    [push]
  );
  const close = useCallback(() => setOpen(false), [setOpen]);
  return { open, push, setOpen, close };
}

const DRAWER_STORAGE_KEY = 'enterprise-search:topics-drawer-open';

function readDrawerOpen(): boolean {
  try {
    return window.localStorage.getItem(DRAWER_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeDrawerOpen(open: boolean): void {
  try {
    window.localStorage.setItem(DRAWER_STORAGE_KEY, String(open));
  } catch {
    // Storage unavailable (private mode); the state just isn't remembered.
  }
}
